import React, { useMemo, useCallback, useState, useReducer, useEffect } from 'react';
import { useClickTracker } from '@shared-ui/clickstream-analytics-context';
import { useDialog } from '@shared-ui/dialog-context';
import { useLocalization } from '@shared-ui/localization-context';
import { UitkFullscreenDialog, UitkDialogContent } from 'uitk-react-dialog';
import { UitkToolbar } from 'uitk-react-toolbar';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useBexApi } from '@shared-ui/bex-api-context';
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLoader } from 'uitk-react-loader';
import { UitkForm } from 'uitk-react-form';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText, UitkHeading, UitkSubheading } from 'uitk-react-text';
import { UitkList, UitkListItem } from 'uitk-react-list';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLink } from 'uitk-react-link';
import { UitkTextInput, UitkTextArea } from 'uitk-react-input';
import { UitkCheckbox, UitkCheckboxGroup } from 'uitk-react-checkbox';
import { UitkRadioButtonGroup, UitkRadioButton } from 'uitk-react-radio-button';
import { UitkPrimaryButton, UitkSecondaryButton } from 'uitk-react-button';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var __assign = function () {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s)
                if (Object.prototype.hasOwnProperty.call(s, p))
                    t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m)
        return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done)
            ar.push(r.value);
    }
    catch (error) {
        e = { error: error };
    }
    finally {
        try {
            if (r && !r.done && (m = i["return"]))
                m.call(i);
        }
        finally {
            if (e)
                throw e.error;
        }
    }
    return ar;
}
function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) {
        Object.defineProperty(cooked, "raw", { value: raw });
    }
    else {
        cooked.raw = raw;
    }
    return cooked;
}

var DirectFeedbackEventType;
(function (DirectFeedbackEventType) {
    DirectFeedbackEventType[DirectFeedbackEventType["Submit"] = 0] = "Submit";
    DirectFeedbackEventType[DirectFeedbackEventType["Close"] = 1] = "Close";
    DirectFeedbackEventType[DirectFeedbackEventType["Error"] = 2] = "Error";
    DirectFeedbackEventType[DirectFeedbackEventType["Load"] = 3] = "Load";
})(DirectFeedbackEventType || (DirectFeedbackEventType = {}));
var DirectFeedbackErrorEvent = /** @class */ (function () {
    function DirectFeedbackErrorEvent(promptId, error) {
        this.type = DirectFeedbackEventType.Error;
        this.promptId = promptId;
        this.error = error;
    }
    return DirectFeedbackErrorEvent;
}());
var DirectFeedbackCloseEvent = /** @class */ (function () {
    function DirectFeedbackCloseEvent(promptId) {
        this.type = DirectFeedbackEventType.Close;
        this.promptId = promptId;
    }
    return DirectFeedbackCloseEvent;
}());
var DirectFeedbackSubmitEvent = /** @class */ (function () {
    function DirectFeedbackSubmitEvent(promptId, context, formValues) {
        this.type = DirectFeedbackEventType.Submit;
        this.promptId = promptId;
        this.context = context;
        this.formValues = formValues;
    }
    return DirectFeedbackSubmitEvent;
}());
var DirectFeedbackLoadEvent = /** @class */ (function () {
    function DirectFeedbackLoadEvent(promptId, data) {
        this.type = DirectFeedbackEventType.Load;
        this.promptId = promptId;
        this.data = data;
    }
    return DirectFeedbackLoadEvent;
}());

/* tslint:disable */
/* eslint-disable */
// @generated
// This file was automatically generated and should not be edited.
//==============================================================
// START Enums and Input Objects
//==============================================================
/**
 * Specifies the behavior of ALTER, which is used to run Abacus experiments on localization messages.
 */
var AlterMode;
(function (AlterMode) {
    AlterMode["DEBUG"] = "DEBUG";
    AlterMode["NONE"] = "NONE";
    AlterMode["PREVIEW"] = "PREVIEW";
    AlterMode["RELEASED"] = "RELEASED";
})(AlterMode || (AlterMode = {}));
var AuthenticationState;
(function (AuthenticationState) {
    AuthenticationState["ANONYMOUS"] = "ANONYMOUS";
    AuthenticationState["AUTHENTICATED"] = "AUTHENTICATED";
    AuthenticationState["IDENTIFIED"] = "IDENTIFIED";
})(AuthenticationState || (AuthenticationState = {}));
var BulletStyle;
(function (BulletStyle) {
    BulletStyle["DEFAULT"] = "DEFAULT";
    BulletStyle["ICON_ALTERNATE"] = "ICON_ALTERNATE";
    BulletStyle["ICON_STANDARD"] = "ICON_STANDARD";
    BulletStyle["NO_BULLET"] = "NO_BULLET";
    BulletStyle["UNRECOGNIZED"] = "UNRECOGNIZED";
})(BulletStyle || (BulletStyle = {}));
/**
 * Valid device types
 */
var DeviceType;
(function (DeviceType) {
    DeviceType["APP_PHONE"] = "APP_PHONE";
    DeviceType["APP_TABLET"] = "APP_TABLET";
    DeviceType["CHAT_APP"] = "CHAT_APP";
    DeviceType["DESKTOP"] = "DESKTOP";
    DeviceType["MOBILE_PHONE"] = "MOBILE_PHONE";
    DeviceType["MOBILE_TABLET"] = "MOBILE_TABLET";
    DeviceType["VOICE_APP"] = "VOICE_APP";
})(DeviceType || (DeviceType = {}));
var DirectFeedbackPageDisplayConditionType;
(function (DirectFeedbackPageDisplayConditionType) {
    DirectFeedbackPageDisplayConditionType["BOOL"] = "BOOL";
    DirectFeedbackPageDisplayConditionType["NONE"] = "NONE";
    DirectFeedbackPageDisplayConditionType["PATTERN"] = "PATTERN";
    DirectFeedbackPageDisplayConditionType["TEXT"] = "TEXT";
})(DirectFeedbackPageDisplayConditionType || (DirectFeedbackPageDisplayConditionType = {}));
/**
 * Defines how checkbox state should be displayed on client.
 */
var EGDSCheckBoxState;
(function (EGDSCheckBoxState) {
    EGDSCheckBoxState["CHECKED"] = "CHECKED";
    EGDSCheckBoxState["INDETERMINATE"] = "INDETERMINATE";
    EGDSCheckBoxState["UNCHECKED"] = "UNCHECKED";
})(EGDSCheckBoxState || (EGDSCheckBoxState = {}));
/**
 * This should only be used in unique design cases where the UX changes frequently and specific product requirements must be met.
 * Most teams should just use String over EGDSHeading or EGDSText.
 *
 * https: // www.figma.com/file/IHAiPyllJIlO6g4f2qdNW4zq/Typography
 */
var EGDSHeadingStyle;
(function (EGDSHeadingStyle) {
    EGDSHeadingStyle["H1"] = "H1";
    EGDSHeadingStyle["H2"] = "H2";
    EGDSHeadingStyle["H3"] = "H3";
    EGDSHeadingStyle["H4"] = "H4";
    EGDSHeadingStyle["H5"] = "H5";
    EGDSHeadingStyle["H6"] = "H6";
    EGDSHeadingStyle["H7"] = "H7";
    EGDSHeadingStyle["H8"] = "H8";
})(EGDSHeadingStyle || (EGDSHeadingStyle = {}));
/**
 * Represents where the icon should be places in a EGDS Standard Link
 */
var EGDSIconPosition;
(function (EGDSIconPosition) {
    EGDSIconPosition["LEADING"] = "LEADING";
    EGDSIconPosition["TRAILING"] = "TRAILING";
})(EGDSIconPosition || (EGDSIconPosition = {}));
/**
 * Represents the size of the text in a EGDS Standard Link
 */
var EGDSLinkSize;
(function (EGDSLinkSize) {
    EGDSLinkSize["LARGE"] = "LARGE";
    EGDSLinkSize["MEDIUM"] = "MEDIUM";
    EGDSLinkSize["SMALL"] = "SMALL";
})(EGDSLinkSize || (EGDSLinkSize = {}));
var EGDSTextListItemStyle;
(function (EGDSTextListItemStyle) {
    EGDSTextListItemStyle["DEFAULT"] = "DEFAULT";
    EGDSTextListItemStyle["EMPHASIS"] = "EMPHASIS";
    EGDSTextListItemStyle["LOYALTY"] = "LOYALTY";
    EGDSTextListItemStyle["NEGATIVE"] = "NEGATIVE";
    EGDSTextListItemStyle["POSITIVE"] = "POSITIVE";
})(EGDSTextListItemStyle || (EGDSTextListItemStyle = {}));
/**
 * supported sizes of icons
 */
var IconSize;
(function (IconSize) {
    IconSize["EXTRA_SMALL"] = "EXTRA_SMALL";
    IconSize["LARGE"] = "LARGE";
    IconSize["MEDIUM"] = "MEDIUM";
    IconSize["MEDIUM_LARGE"] = "MEDIUM_LARGE";
    IconSize["SMALL"] = "SMALL";
    IconSize["XLARGE"] = "XLARGE";
})(IconSize || (IconSize = {}));
/**
 * Supported themes of icons. See the prop definitions here: http: // uitk-react.tools.expedia.com/expedia/en_US/components/icons
 */
var IconTheme;
(function (IconTheme) {
    IconTheme["default"] = "default";
    IconTheme["emphasis"] = "emphasis";
    IconTheme["negative"] = "negative";
    IconTheme["positive"] = "positive";
})(IconTheme || (IconTheme = {}));
/**
 * Privacy tracking user preference. We can retrieve info on if the site allows tracking from Site Info and null can also be used to represent a selection or not
 */
var PrivacyTrackingState;
(function (PrivacyTrackingState) {
    PrivacyTrackingState["CAN_NOT_TRACK"] = "CAN_NOT_TRACK";
    PrivacyTrackingState["CAN_TRACK"] = "CAN_TRACK";
})(PrivacyTrackingState || (PrivacyTrackingState = {}));
/**
 * Specifies whether to open the link in the same user interface context or in a different context
 */
var UILinkTarget;
(function (UILinkTarget) {
    UILinkTarget["EXTERNAL"] = "EXTERNAL";
    UILinkTarget["INTERNAL"] = "INTERNAL";
})(UILinkTarget || (UILinkTarget = {}));
//==============================================================
// END Enums and Input Objects
//==============================================================

// eslint-disable-next-line max-classes-per-file
var DirectFeedbackCommandType;
(function (DirectFeedbackCommandType) {
    DirectFeedbackCommandType[DirectFeedbackCommandType["Submit"] = 0] = "Submit";
    DirectFeedbackCommandType[DirectFeedbackCommandType["Previous"] = 1] = "Previous";
    DirectFeedbackCommandType[DirectFeedbackCommandType["Next"] = 2] = "Next";
    DirectFeedbackCommandType[DirectFeedbackCommandType["SetValue"] = 3] = "SetValue";
})(DirectFeedbackCommandType || (DirectFeedbackCommandType = {}));
var DirectFeedbackSubmitCommand = /** @class */ (function () {
    // eslint-disable-next-line no-useless-constructor,@typescript-eslint/no-empty-function
    function DirectFeedbackSubmitCommand(action) {
        this.type = DirectFeedbackCommandType.Submit;
        this.action = action;
    }
    return DirectFeedbackSubmitCommand;
}());
var DirectFeedbackPreviousCommand = /** @class */ (function () {
    // eslint-disable-next-line no-useless-constructor,@typescript-eslint/no-empty-function
    function DirectFeedbackPreviousCommand(action) {
        this.type = DirectFeedbackCommandType.Previous;
        this.action = action;
    }
    return DirectFeedbackPreviousCommand;
}());
var DirectFeedbackNextCommand = /** @class */ (function () {
    // eslint-disable-next-line no-useless-constructor,@typescript-eslint/no-empty-function
    function DirectFeedbackNextCommand(action) {
        this.type = DirectFeedbackCommandType.Next;
        this.action = action;
    }
    return DirectFeedbackNextCommand;
}());
var DirectFeedbackSetValueCommand = /** @class */ (function () {
    // eslint-disable-next-line no-useless-constructor,@typescript-eslint/no-empty-function
    function DirectFeedbackSetValueCommand(fieldId, value, isValid) {
        this.type = DirectFeedbackCommandType.SetValue;
        this.fieldId = fieldId;
        this.value = value;
        this.isValid = isValid;
    }
    return DirectFeedbackSetValueCommand;
}());

/**
 * @protected
 */
var fragment$q = gql(templateObject_1$r || (templateObject_1$r = __makeTemplateObject(["\n  fragment EGDSIconFragment on Icon {\n    __typename\n    description\n    id\n    size\n    theme\n    title\n    withBackground\n  }\n"], ["\n  fragment EGDSIconFragment on Icon {\n    __typename\n    description\n    id\n    size\n    theme\n    title\n    withBackground\n  }\n"])));
var idIncrementor$2 = 0;
var EGDSIcon = function (_a) {
    var _b, _c;
    var data = _a.data;
    /**
     * The behavior we want is to generate a component-unique ID on mount, and not on rerender.
     * This is why we do not supply dependencies in the array (DependencyList).
     */
    var id = useMemo(function () {
        idIncrementor$2 += 1;
        return idIncrementor$2;
    }, []);
    return (React.createElement(UitkIcon, { description: data.description, name: data.id, id: "uitk-icon-" + data.id + "-" + id, size: data.size ? UitkIconSize[data.size] : undefined, title: (_b = data.title) !== null && _b !== void 0 ? _b : undefined, theme: (_c = data.theme) !== null && _c !== void 0 ? _c : undefined, shadow: Boolean(data.withBackground) }));
};
EGDSIcon.displayName = 'EGDSIcon';
EGDSIcon.fragment = fragment$q;
var templateObject_1$r;

var fragment$p = gql(templateObject_1$q || (templateObject_1$q = __makeTemplateObject(["\n  fragment EGDSIconTextFragment on EGDSIconText {\n    icon {\n      ...EGDSIconFragment\n    }\n    text\n  }\n\n  ", "\n"], ["\n  fragment EGDSIconTextFragment on EGDSIconText {\n    icon {\n      ...EGDSIconFragment\n    }\n    text\n  }\n\n  ", "\n"])), EGDSIcon.fragment);
var EGDSIconText = function (_a) {
    var data = _a.data;
    var icon = data.icon, text = data.text;
    return (React.createElement(React.Fragment, null,
        React.createElement(UitkSpacing, { margin: { inlineend: 'one' } },
            React.createElement(EGDSIcon, { data: icon })),
        React.createElement(UitkSpacing, { margin: { inlineend: 'one' } },
            React.createElement(UitkText, { inline: true }, text))));
};
EGDSIconText.fragment = fragment$p;
EGDSIconText.displayName = 'EGDSIconText';
var templateObject_1$q;

var _a$2, _b;
var fragment$o = gql(templateObject_1$p || (templateObject_1$p = __makeTemplateObject(["\n  fragment EGDSInlineLinkFragment on EGDSInlineLink {\n    action {\n      accessibility\n      analytics {\n        linkName\n        referrerId\n      }\n      resource {\n        value\n      }\n      target\n    }\n    disabled\n    size\n    text\n  }\n"], ["\n  fragment EGDSInlineLinkFragment on EGDSInlineLink {\n    action {\n      accessibility\n      analytics {\n        linkName\n        referrerId\n      }\n      resource {\n        value\n      }\n      target\n    }\n    disabled\n    size\n    text\n  }\n"])));
var linkSizeMapper = (_a$2 = {},
    _a$2[EGDSLinkSize.SMALL] = 'small',
    _a$2[EGDSLinkSize.MEDIUM] = 'medium',
    _a$2[EGDSLinkSize.LARGE] = 'large',
    _a$2);
var UILinkTargetMap = (_b = {},
    _b[UILinkTarget.EXTERNAL] = '_blank',
    _b[UILinkTarget.INTERNAL] = '_self',
    _b);
var EGDSInlineLink = function (_a) {
    var _b = _a.data, _c = _b.action, analytics = _c.analytics, resource = _c.resource, target = _c.target, accessibility = _c.accessibility, disabled = _b.disabled; _b.size; var text = _b.text;
    // TODO: Analytics via clickstream. CDF-299
    var track = useClickTracker();
    var uitkLinkAnalytics = useMemo(function () {
        return {
            id: analytics.referrerId,
            description: analytics.linkName,
            callback: function (id, description, event) {
                track(id, description);
            }
        };
    }, [analytics, track]);
    return (React.createElement(UitkLink, { size: "large", disabled: disabled, analytics: uitkLinkAnalytics, inline: true },
        React.createElement("a", { href: resource.value, target: UILinkTargetMap[target] },
            text,
            accessibility && React.createElement("span", { className: "is-visually-hidden" }, accessibility))));
};
EGDSInlineLink.fragment = fragment$o;
EGDSInlineLink.displayName = 'EGDSInlineLink';
var templateObject_1$p;

var fragment$n = gql(templateObject_1$o || (templateObject_1$o = __makeTemplateObject(["\n  fragment EGDSPlainTextFragment on EGDSPlainText {\n    __typename\n    text\n  }\n"], ["\n  fragment EGDSPlainTextFragment on EGDSPlainText {\n    __typename\n    text\n  }\n"])));
var EGDSPlainText = function (_a) {
    var data = _a.data, _b = _a.inline, inline = _b === void 0 ? false : _b;
    return (React.createElement(UitkText, { size: 400, inline: inline }, data.text));
};
EGDSPlainText.fragment = fragment$n;
EGDSPlainText.displayName = 'EGDSPlainText';
var templateObject_1$o;

var fragment$m = gql(templateObject_1$n || (templateObject_1$n = __makeTemplateObject(["\n  fragment EGDSStandardLinkFragment on EGDSStandardLink {\n    action {\n      accessibility\n      analytics {\n        linkName\n        referrerId\n      }\n      resource {\n        value\n      }\n      target\n    }\n    disabled\n    standardLinkIcon: icon {\n      ...EGDSIconFragment\n    }\n    iconPosition\n    size\n    text\n  }\n\n  ", "\n"], ["\n  fragment EGDSStandardLinkFragment on EGDSStandardLink {\n    action {\n      accessibility\n      analytics {\n        linkName\n        referrerId\n      }\n      resource {\n        value\n      }\n      target\n    }\n    disabled\n    standardLinkIcon: icon {\n      ...EGDSIconFragment\n    }\n    iconPosition\n    size\n    text\n  }\n\n  ", "\n"])), EGDSIcon.fragment);
var IconPlacement = function (_a) {
    var icon = _a.icon, iconPosition = _a.iconPosition, iconPositionToMatch = _a.iconPositionToMatch;
    if (icon && iconPosition === iconPositionToMatch) {
        return React.createElement(EGDSIcon, { data: icon });
    }
    return null;
};
var EGDSStandardLink = function (_a) {
    // TODO: Analytics via clickstream. CDF-299
    var className = _a.className, inline = _a.inline, _b = _a.data, _c = _b.action, resource = _c.resource, target = _c.target, accessibility = _c.accessibility, disabled = _b.disabled, iconPosition = _b.iconPosition, standardLinkIcon = _b.standardLinkIcon, size = _b.size, text = _b.text;
    return (React.createElement(UitkLink, { className: className, size: linkSizeMapper[size], inline: inline, disabled: disabled },
        React.createElement("a", { target: UILinkTargetMap[target], href: resource.value },
            React.createElement(IconPlacement, { icon: standardLinkIcon, iconPosition: iconPosition, iconPositionToMatch: EGDSIconPosition.LEADING }),
            text,
            React.createElement(IconPlacement, { icon: standardLinkIcon, iconPosition: iconPosition, iconPositionToMatch: EGDSIconPosition.TRAILING }),
            accessibility && React.createElement(UitkText, { className: "is-visually-hidden" }, accessibility))));
};
EGDSStandardLink.fragment = fragment$m;
EGDSStandardLink.displayName = 'EGDSStandardLink';
var templateObject_1$n;

var fragment$l = gql(templateObject_1$m || (templateObject_1$m = __makeTemplateObject(["\n  fragment EGDSTextFragment on EGDSText {\n    __typename\n    text\n    ...EGDSPlainTextFragment\n    ...EGDSInlineLinkFragment\n    ...EGDSStandardLinkFragment\n    ...EGDSIconTextFragment\n  }\n\n  ", "\n  ", "\n  ", "\n  ", "\n"], ["\n  fragment EGDSTextFragment on EGDSText {\n    __typename\n    text\n    ...EGDSPlainTextFragment\n    ...EGDSInlineLinkFragment\n    ...EGDSStandardLinkFragment\n    ...EGDSIconTextFragment\n  }\n\n  ", "\n  ", "\n  ", "\n  ", "\n"])), EGDSIconText.fragment, EGDSPlainText.fragment, EGDSInlineLink.fragment, EGDSStandardLink.fragment);
/**
 * For Reusability, lookups like this should probably be provided via a context. This will allow
 * for UITK components to be specified in a reusable package, but for components in the model
 * that have been declared as an interface and may have implementations dependent on the
 * domain/usecase they can be registered/provided while still taking advantage of the other "built in"
 * types.
 */
var EGDSTextConcreteTypeLookup = {
    EGDSPlainText: EGDSPlainText,
    EGDSInlineLink: EGDSInlineLink,
    EGDSStandardLink: EGDSStandardLink,
    EGDSIconText: EGDSIconText
};
var EGDSText = function (_a) {
    var data = _a.data, _b = _a.inline, inline = _b === void 0 ? false : _b;
    var Component = EGDSTextConcreteTypeLookup[data.__typename] || EGDSPlainText;
    return React.createElement(Component, { inline: inline, data: data });
};
EGDSText.fragment = fragment$l;
EGDSText.displayName = 'EGDSText';
var templateObject_1$m;

var fragment$k = gql(templateObject_1$l || (templateObject_1$l = __makeTemplateObject(["\n  fragment EGDSSpannableTextFragment on EGDSSpannableText {\n    __typename\n    text\n    egdsElementId\n    contents {\n      ...EGDSTextFragment\n    }\n  }\n\n  ", "\n"], ["\n  fragment EGDSSpannableTextFragment on EGDSSpannableText {\n    __typename\n    text\n    egdsElementId\n    contents {\n      ...EGDSTextFragment\n    }\n  }\n\n  ", "\n"])), EGDSText.fragment);
var EGDSSpannableText = function (_a) {
    var data = _a.data;
    return (React.createElement(React.Fragment, null, data.contents ? (data.contents.map(function (element, index) { return React.createElement(EGDSText, { inline: true, data: element, key: "EGDSSpannableTextContents_" + index }); })) : (React.createElement(EGDSText, { data: data }))));
};
EGDSSpannableText.fragment = fragment$k;
EGDSSpannableText.displayName = 'EGDSSpannableText';
var templateObject_1$l;

var _a$1;
var fragment$j = gql(templateObject_1$k || (templateObject_1$k = __makeTemplateObject(["\n  fragment DirectFeedbackTextListFragment on DirectFeedbackTextList {\n    __typename\n    bullet\n    listItems {\n      egdsElementId\n      icon {\n        ...EGDSIconFragment\n      }\n      text {\n        ...EGDSSpannableTextFragment\n      }\n      style\n    }\n  }\n\n  ", "\n  ", "\n"], ["\n  fragment DirectFeedbackTextListFragment on DirectFeedbackTextList {\n    __typename\n    bullet\n    listItems {\n      egdsElementId\n      icon {\n        ...EGDSIconFragment\n      }\n      text {\n        ...EGDSSpannableTextFragment\n      }\n      style\n    }\n  }\n\n  ", "\n  ", "\n"])), EGDSIcon.fragment, EGDSSpannableText.fragment);
var bulletStyle = (_a$1 = {},
    _a$1[BulletStyle.DEFAULT] = 'default',
    _a$1[BulletStyle.ICON_ALTERNATE] = 'icon-alternate',
    _a$1[BulletStyle.ICON_STANDARD] = 'icon-standard',
    _a$1[BulletStyle.NO_BULLET] = 'no-bullet',
    _a$1[BulletStyle.UNRECOGNIZED] = undefined,
    _a$1);
var DirectFeedbackTextList = function (_a) {
    var data = _a.data;
    return data.listItems.length ? (React.createElement(UitkList, { bullet: bulletStyle[data.bullet] }, data.listItems.map(function (_a) {
        var icon = _a.icon, egdsElementId = _a.egdsElementId, text = _a.text;
        return (React.createElement(UitkListItem, { key: egdsElementId },
            icon ? React.createElement(EGDSIcon, { data: icon }) : null,
            text.__typename === 'EGDSSpannableText' && React.createElement(EGDSSpannableText, { data: text })));
    }))) : null;
};
DirectFeedbackTextList.fragment = fragment$j;
DirectFeedbackTextList.displayName = 'DirectFeedbackTextList';
var templateObject_1$k;

var fragment$i = gql(templateObject_1$j || (templateObject_1$j = __makeTemplateObject(["\n  fragment EGDSErrorSummaryFragment on EGDSErrorSummary {\n    __typename\n    heading\n    body {\n      text\n    }\n  }\n"], ["\n  fragment EGDSErrorSummaryFragment on EGDSErrorSummary {\n    __typename\n    heading\n    body {\n      text\n    }\n  }\n"])));
var EGDSErrorSummary = function (_a) {
    var _b = _a.data, heading = _b.heading, body = _b.body;
    var description = body === null || body === void 0 ? void 0 : body.text;
    return React.createElement(UitkErrorSummary, { heading: heading, description: description });
};
EGDSErrorSummary.displayName = 'ErrorSummary';
EGDSErrorSummary.fragment = fragment$i;
EGDSErrorSummary.fragment.__typename = 'EGDSErrorSummary';
var templateObject_1$j;

var _a;
var fragment$h = gql(templateObject_1$i || (templateObject_1$i = __makeTemplateObject(["\n  fragment EGDSHeadingFragment on EGDSHeading {\n    __typename\n    text\n    headingType\n  }\n"], ["\n  fragment EGDSHeadingFragment on EGDSHeading {\n    __typename\n    text\n    headingType\n  }\n"])));
var headingTagMapper = (_a = {},
    _a[EGDSHeadingStyle.H1] = 'h1',
    _a[EGDSHeadingStyle.H2] = 'h2',
    _a[EGDSHeadingStyle.H3] = 'h3',
    _a[EGDSHeadingStyle.H4] = 'h4',
    _a[EGDSHeadingStyle.H5] = 'h5',
    _a[EGDSHeadingStyle.H6] = 'h6',
    _a[EGDSHeadingStyle.H7] = 'h6',
    _a[EGDSHeadingStyle.H8] = 'h6',
    _a);
var EGDSHeading = function (_a) {
    var data = _a.data;
    return (React.createElement(UitkHeading, { tag: data.headingType ? headingTagMapper[data.headingType] : undefined }, data.text));
};
EGDSHeading.fragment = fragment$h;
EGDSHeading.displayName = 'EGDSHeading';
var templateObject_1$i;

var fragment$g = gql(templateObject_1$h || (templateObject_1$h = __makeTemplateObject(["\n  fragment DirectFeedbackDisplayElementFragment on DirectFeedbackFormDisplayElement {\n    __typename\n    ...EGDSErrorSummaryFragment\n    ...EGDSPlainTextFragment\n    ...EGDSHeadingFragment\n    ...EGDSSpannableTextFragment\n    ...DirectFeedbackTextListFragment\n  }\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"], ["\n  fragment DirectFeedbackDisplayElementFragment on DirectFeedbackFormDisplayElement {\n    __typename\n    ...EGDSErrorSummaryFragment\n    ...EGDSPlainTextFragment\n    ...EGDSHeadingFragment\n    ...EGDSSpannableTextFragment\n    ...DirectFeedbackTextListFragment\n  }\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"])), EGDSErrorSummary.fragment, EGDSPlainText.fragment, EGDSHeading.fragment, EGDSSpannableText.fragment, DirectFeedbackTextList.fragment);
var EgdsDisplayFragmentLookup = {
    EGDSPlainText: EGDSPlainText,
    EGDSHeading: EGDSHeading,
    EGDSSpannableText: EGDSSpannableText,
    DirectFeedbackTextList: DirectFeedbackTextList,
    EGDSErrorSummary: EGDSErrorSummary
};
var DirectFeedbackDisplayElement = function (props) {
    var data = props.data;
    var DisplayComponentImplementation = EgdsDisplayFragmentLookup[data.__typename];
    if (DisplayComponentImplementation) {
        return React.createElement(DisplayComponentImplementation, { data: data });
    }
    return React.createElement("pre", null, JSON.stringify(data, null, 2));
};
DirectFeedbackDisplayElement.fragment = fragment$g;
DirectFeedbackDisplayElement.displayName = 'DirectFeedbackDisplayElement';
DirectFeedbackDisplayElement.supportedTypes = Object.keys(EgdsDisplayFragmentLookup);
var templateObject_1$h;

var fragment$f = gql(templateObject_1$g || (templateObject_1$g = __makeTemplateObject(["\n  fragment EGDSMaxLengthInputValidationFragment on EGDSMaxLengthInputValidation {\n    errorMessage\n    maxLength\n  }\n"], ["\n  fragment EGDSMaxLengthInputValidationFragment on EGDSMaxLengthInputValidation {\n    errorMessage\n    maxLength\n  }\n"])));
var defaultValidator$3 = function (value, validation) {
    if (value && value.length > validation.maxLength)
        return { invalid: validation.errorMessage };
    return {};
};
var EGDSMaxLengthInputValidation = function (validation) {
    return {
        validate: function (value) {
            return defaultValidator$3(value, validation);
        }
    };
};
EGDSMaxLengthInputValidation.fragment = fragment$f;
var templateObject_1$g;

var fragment$e = gql(templateObject_1$f || (templateObject_1$f = __makeTemplateObject(["\n  fragment EGDSMinLengthInputValidationFragment on EGDSMinLengthInputValidation {\n    errorMessage\n    minLength\n  }\n"], ["\n  fragment EGDSMinLengthInputValidationFragment on EGDSMinLengthInputValidation {\n    errorMessage\n    minLength\n  }\n"])));
var defaultValidator$2 = function (value, validation) {
    if (value && value.length < validation.minLength)
        return { invalid: validation.errorMessage };
    return {};
};
var EGDSMinLengthInputValidation = function (validation) {
    return {
        validate: function (value) {
            return defaultValidator$2(value, validation);
        }
    };
};
EGDSMinLengthInputValidation.fragment = fragment$e;
var templateObject_1$f;

var fragment$d = gql(templateObject_1$e || (templateObject_1$e = __makeTemplateObject(["\n  fragment EGDSRegexInputValidationFragment on EGDSRegexInputValidation {\n    errorMessage\n    pattern\n  }\n"], ["\n  fragment EGDSRegexInputValidationFragment on EGDSRegexInputValidation {\n    errorMessage\n    pattern\n  }\n"])));
var defaultValidator$1 = function (value, validation) {
    var re = new RegExp(validation.pattern);
    if ((value === null || value === void 0 ? void 0 : value.trim()) && !value.match(re))
        return { invalid: validation.errorMessage };
    return {};
};
var EGDSRegexInputValidation = function (validation) {
    return {
        validate: function (value) {
            return defaultValidator$1(value, validation);
        }
    };
};
EGDSRegexInputValidation.fragment = fragment$d;
var templateObject_1$e;

var fragment$c = gql(templateObject_1$d || (templateObject_1$d = __makeTemplateObject(["\n  fragment EGDSRequiredInputValidationFragment on EGDSRequiredInputValidation {\n    errorMessage\n  }\n"], ["\n  fragment EGDSRequiredInputValidationFragment on EGDSRequiredInputValidation {\n    errorMessage\n  }\n"])));
var defaultValidator = function (value, validation) {
    if (!(value === null || value === void 0 ? void 0 : value.trim()))
        return { invalid: validation.errorMessage };
    return {};
};
var EGDSRequiredInputValidation = function (validation) {
    return {
        validate: function (value) {
            return defaultValidator(value, validation);
        }
    };
};
EGDSRequiredInputValidation.fragment = fragment$c;
var templateObject_1$d;

var fragment$b = gql(templateObject_1$c || (templateObject_1$c = __makeTemplateObject(["\n  fragment EGDSInputValidationFragment on EGDSInputValidation {\n    ...EGDSRegexInputValidationFragment\n    ...EGDSMaxLengthInputValidationFragment\n    ...EGDSMinLengthInputValidationFragment\n    ...EGDSRequiredInputValidationFragment\n  }\n\n  ", "\n  ", "\n  ", "\n  ", "\n"], ["\n  fragment EGDSInputValidationFragment on EGDSInputValidation {\n    ...EGDSRegexInputValidationFragment\n    ...EGDSMaxLengthInputValidationFragment\n    ...EGDSMinLengthInputValidationFragment\n    ...EGDSRequiredInputValidationFragment\n  }\n\n  ", "\n  ", "\n  ", "\n  ", "\n"])), EGDSRegexInputValidation.fragment, EGDSRequiredInputValidation.fragment, EGDSMaxLengthInputValidation.fragment, EGDSMinLengthInputValidation.fragment);
var getValidators = function (validations) {
    return validations.map(function (validation) {
        switch (validation.__typename) {
            case 'EGDSRegexInputValidation':
                return __assign(__assign({}, validation), EGDSRegexInputValidation(validation));
            case 'EGDSMaxLengthInputValidation':
                return __assign(__assign({}, validation), EGDSMaxLengthInputValidation(validation));
            case 'EGDSMinLengthInputValidation':
                return __assign(__assign({}, validation), EGDSMinLengthInputValidation(validation));
            case 'EGDSRequiredInputValidation':
                return __assign(__assign({}, validation), EGDSRequiredInputValidation(validation));
            default:
                return { validate: function () { return ({}); } };
        }
    });
};
var getValidator = function (validations) {
    var validators = getValidators(validations || []);
    return {
        validate: function (value) {
            return validators.map(function (validator) { return validator.validate(value); }).find(function (result) { return result === null || result === void 0 ? void 0 : result.invalid; }) || {};
        }
    };
};
var templateObject_1$c;

var fragment$a = gql(templateObject_1$b || (templateObject_1$b = __makeTemplateObject(["\n  fragment DirectFeedbackInputFieldFragment on DirectFeedbackInputField {\n    __typename\n    label\n    value\n    name\n    placeholder\n    validations {\n      ...EGDSInputValidationFragment\n    }\n    instructions\n    leftIcon {\n      ...EGDSIconFragment\n    }\n    rightIcon {\n      ...EGDSIconFragment\n    }\n    required\n  }\n  ", "\n  ", "\n"], ["\n  fragment DirectFeedbackInputFieldFragment on DirectFeedbackInputField {\n    __typename\n    label\n    value\n    name\n    placeholder\n    validations {\n      ...EGDSInputValidationFragment\n    }\n    instructions\n    leftIcon {\n      ...EGDSIconFragment\n    }\n    rightIcon {\n      ...EGDSIconFragment\n    }\n    required\n  }\n  ", "\n  ", "\n"])), fragment$b, EGDSIcon.fragment);
var idIncrementor$1 = 0;
var DirectFeedbackInputField = function (props) {
    var _a = props.data, label = _a.label, required = _a.required, placeholder = _a.placeholder, dataValue = _a.value, name = _a.name, leftIcon = _a.leftIcon, validations = _a.validations, onChange = props.onChange, value = props.value;
    // TODO: Right Icon Support. Right Icons not defined for text input. May implement by passing as children.
    /**
     * The behavior we want is to generate a component-unique ID on mount, and not on rerender.
     * This is why we do not supply dependencies in the array (DependencyList).
     */
    var id = useMemo(function () {
        idIncrementor$1 += 1;
        return idIncrementor$1;
    }, []);
    var validator = useMemo(function () {
        return getValidator(validations);
    }, [validations]);
    var handleChange = useCallback(function (e) {
        if (onChange) {
            onChange(e.target.name, e.target.value, !('invalid' in validator.validate(e.target.value)) && (!required || !!e.target.value));
        }
    }, [onChange, required, validator]);
    return (React.createElement(UitkTextInput, { id: "uitk-text-input-" + name + "-" + id, label: label || '', value: value || dataValue || undefined, name: name || undefined, hasVisibleLabel: label !== '', placeholder: placeholder || undefined, required: required || false, onChange: handleChange, icon: (leftIcon === null || leftIcon === void 0 ? void 0 : leftIcon.id) || undefined }));
};
DirectFeedbackInputField.fragment = fragment$a;
DirectFeedbackInputField.displayName = 'DirectFeedbackInputField';
var templateObject_1$b;

var fragment$9 = gql(templateObject_1$a || (templateObject_1$a = __makeTemplateObject(["\n  fragment EGDSCheckBoxFragment on EGDSCheckBox {\n    __typename\n    description\n    enabled\n    errorMessage\n    label {\n      text\n    }\n    name\n    required\n    state\n  }\n"], ["\n  fragment EGDSCheckBoxFragment on EGDSCheckBox {\n    __typename\n    description\n    enabled\n    errorMessage\n    label {\n      text\n    }\n    name\n    required\n    state\n  }\n"])));
var EGDSCheckBox = function (_a) {
    var _b;
    var data = _a.data, onChange = _a.onChange, checked = _a.checked, required = _a.required;
    return (React.createElement(UitkCheckbox, { checked: checked !== null ? checked : data.state === EGDSCheckBoxState.CHECKED, indeterminate: checked !== null && data.state === EGDSCheckBoxState.INDETERMINATE, description: data.description || undefined, required: data.required || required, label: (_b = data.label) === null || _b === void 0 ? void 0 : _b.text, disabled: !data.enabled, name: data.name || undefined, id: data.name || undefined, errorMessage: data.errorMessage || undefined, onChange: function (e) {
            if (onChange) {
                onChange(data.name, e.currentTarget.checked);
            }
        } }, data.label.text));
};
EGDSCheckBox.fragment = fragment$9;
EGDSCheckBox.displayName = 'EGDSCheckBox';
var templateObject_1$a;

var fragment$8 = gql(templateObject_1$9 || (templateObject_1$9 = __makeTemplateObject(["\n  fragment DirectFeedbackMultiSelectGroupFragment on DirectFeedbackMultiSelectGroup {\n    __typename\n    errorMessage\n    groupName\n    multiSelectOptions: options {\n      ...EGDSCheckBoxFragment\n    }\n    selected\n    required\n    legend\n  }\n  ", "\n"], ["\n  fragment DirectFeedbackMultiSelectGroupFragment on DirectFeedbackMultiSelectGroup {\n    __typename\n    errorMessage\n    groupName\n    multiSelectOptions: options {\n      ...EGDSCheckBoxFragment\n    }\n    selected\n    required\n    legend\n  }\n  ", "\n"])), EGDSCheckBox.fragment);
var reducer = function (state, event) {
    var values = state.values;
    if (!event.value) {
        values = values.filter(function (name) { return name !== event.name; });
    }
    else if (!state.values.includes(event.name)) {
        values = values.concat([event.name]);
    }
    return { values: values };
};
var DirectFeedbackMultiSelectGroup = function (_a) {
    var _b;
    var data = _a.data, onChange = _a.onChange, value = _a.value;
    var groupName = data.groupName, errorMessage = data.errorMessage, options = data.multiSelectOptions, required = data.required, legend = data.legend;
    var _c = __read(useState({ values: value || ((_b = data.selected) === null || _b === void 0 ? void 0 : _b.split(',')) || [] }), 2), state = _c[0], setState = _c[1];
    var handleCheckChange = useCallback(function (name, newValue) {
        var _a;
        /**
         * If there is ever a case where multiple checkboxes can be toggled in the same tick,
         * we will need to switch this to a reducer and find a clean way to trigger onChange
         * outside of that reducer (or timeout callback).
         */
        var newState = reducer(state, { name: name, value: newValue });
        setState(newState);
        if (onChange)
            onChange(groupName, newState.values, !required || !!((_a = newState.values) === null || _a === void 0 ? void 0 : _a.length));
    }, [onChange, groupName, required, state]);
    return (React.createElement(UitkCheckboxGroup, { group: groupName, errorMessage: errorMessage || undefined, legendLabel: legend }, options.map(function (option, index) {
        return (React.createElement(EGDSCheckBox, { checked: state.values.includes(option.name || ''), required: data.required ? state.values.length === 0 : undefined, data: option, key: option.name + "_" + index, onChange: handleCheckChange }));
    })));
};
DirectFeedbackMultiSelectGroup.fragment = fragment$8;
DirectFeedbackMultiSelectGroup.displayName = 'DirectFeedbackMultiSelectGroup';
var templateObject_1$9;

var fragment$7 = gql(templateObject_1$8 || (templateObject_1$8 = __makeTemplateObject(["\n  fragment DirectFeedbackRadioGroupFragment on DirectFeedbackRadioGroup {\n    __typename\n    errorMessage\n    groupName\n    radioGroupOptions: options {\n      description\n      disabled\n      label\n      labelSuffix\n      value\n    }\n    selected\n    required\n    legend\n  }\n"], ["\n  fragment DirectFeedbackRadioGroupFragment on DirectFeedbackRadioGroup {\n    __typename\n    errorMessage\n    groupName\n    radioGroupOptions: options {\n      description\n      disabled\n      label\n      labelSuffix\n      value\n    }\n    selected\n    required\n    legend\n  }\n"])));
var DirectFeedbackRadioGroup = function (_a) {
    var data = _a.data, onChange = _a.onChange, value = _a.value;
    var groupName = data.groupName, errorMessage = data.errorMessage, options = data.radioGroupOptions, required = data.required, legend = data.legend;
    var _b = __read(useState({ value: value || data.selected || undefined }), 2), state = _b[0], setState = _b[1];
    var handleRadioChange = useCallback(function (event) {
        var newState = { value: event.currentTarget.value };
        setState(newState);
        if (onChange)
            onChange(groupName, newState.value, !required || !!newState.value);
    }, [onChange, groupName, required]);
    return (React.createElement(UitkRadioButtonGroup, { group: groupName, errorMessage: errorMessage || undefined, selected: state.value, onChange: handleRadioChange, legendLabel: legend }, options.map(function (option, index) {
        return (React.createElement(UitkSpacing, { key: groupName + "_option" + index, margin: { blockend: 'two' } },
            React.createElement("div", null,
                React.createElement(UitkRadioButton, { id: option.value || groupName + "_option" + index, value: option.value || groupName + "_option" + index, labelSuffix: option.labelSuffix || undefined, description: option.description || undefined }, option.label))));
    })));
};
DirectFeedbackRadioGroup.fragment = fragment$7;
DirectFeedbackRadioGroup.displayName = 'DirectFeedbackRadioGroup';
var templateObject_1$8;

var fragment$6 = gql(templateObject_1$7 || (templateObject_1$7 = __makeTemplateObject(["\n  fragment DirectFeedbackTextAreaFragment on DirectFeedbackTextArea {\n    __typename\n    label\n    value\n    name\n    placeholder\n    required\n    isEditable\n    minRows\n    maxRows\n    validations {\n      ...EGDSInputValidationFragment\n    }\n    instructions\n    leftIcon {\n      ...EGDSIconFragment\n    }\n    rightIcon {\n      ...EGDSIconFragment\n    }\n  }\n\n  ", "\n  ", "\n"], ["\n  fragment DirectFeedbackTextAreaFragment on DirectFeedbackTextArea {\n    __typename\n    label\n    value\n    name\n    placeholder\n    required\n    isEditable\n    minRows\n    maxRows\n    validations {\n      ...EGDSInputValidationFragment\n    }\n    instructions\n    leftIcon {\n      ...EGDSIconFragment\n    }\n    rightIcon {\n      ...EGDSIconFragment\n    }\n  }\n\n  ", "\n  ", "\n"])), fragment$b, EGDSIcon.fragment);
var idIncrementor = 0;
/**
 * Adapted from https://github.expedia.biz/egds/components-android/blob/master/markdown/components/EGDSTextArea/EGDSTextArea.md
 * as the TextArea is not provided by egds-components-kotlin. This adaptation is provided by DirectFeedbackTextArea in experience-api-content-direct-feedback
 * and implements EGDSInputField.
 *
 * @property label
 * @property value
 * @property placeholder
 * @property isEditable
 * @property errorMessage
 * @property minRows
 * @property maxRows
 * @property validation
 * @property instructions
 * @property leftIcon
 * @property rightIcon
 */
var DirectFeedbackTextArea = function (props) {
    var _a = props.data, isEditable = _a.isEditable, label = _a.label, maxRows = _a.maxRows, minRows = _a.minRows, placeholder = _a.placeholder, dataValue = _a.value, name = _a.name, required = _a.required, validations = _a.validations, onChange = props.onChange, value = props.value;
    /**
     * The behavior we want is to generate a component-unique ID on mount, and not on rerender.
     * This is why we do not supply dependencies in the array (DependencyList).
     */
    var id = useMemo(function () {
        idIncrementor += 1;
        return idIncrementor;
    }, []);
    var validator = useMemo(function () {
        return getValidator(validations);
    }, [validations]);
    var handleChange = useCallback(function (e) {
        if (onChange) {
            onChange(e.target.name, e.target.value, !('invalid' in validator.validate(e.target.value)) && (!required || !!e.target.value));
        }
    }, [onChange, required, validator]);
    return (React.createElement(UitkTextArea, { id: "uitk-text-area-" + name + "-" + id, label: label || '', value: value || dataValue || undefined, name: name || undefined, disabled: !isEditable, hasVisibleLabel: label !== '', maxRows: maxRows || undefined, minRows: minRows || undefined, placeholder: placeholder || undefined, onChange: handleChange, required: required || false }));
};
DirectFeedbackTextArea.fragment = fragment$6;
DirectFeedbackTextArea.displayName = 'DirectFeedbackTextArea';
var templateObject_1$7;

var fragment$5 = gql(templateObject_1$6 || (templateObject_1$6 = __makeTemplateObject(["\n  fragment DirectFeedbackElementFragment on DirectFeedbackFormPageElement {\n    __typename\n    ...EGDSErrorSummaryFragment\n    ...EGDSPlainTextFragment\n    ...EGDSHeadingFragment\n    ...DirectFeedbackMultiSelectGroupFragment\n    ...EGDSSpannableTextFragment\n    ...DirectFeedbackRadioGroupFragment\n    ...DirectFeedbackInputFieldFragment\n    ...DirectFeedbackTextAreaFragment\n    ...DirectFeedbackTextListFragment\n  }\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"], ["\n  fragment DirectFeedbackElementFragment on DirectFeedbackFormPageElement {\n    __typename\n    ...EGDSErrorSummaryFragment\n    ...EGDSPlainTextFragment\n    ...EGDSHeadingFragment\n    ...DirectFeedbackMultiSelectGroupFragment\n    ...EGDSSpannableTextFragment\n    ...DirectFeedbackRadioGroupFragment\n    ...DirectFeedbackInputFieldFragment\n    ...DirectFeedbackTextAreaFragment\n    ...DirectFeedbackTextListFragment\n  }\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"])), EGDSErrorSummary.fragment, EGDSPlainText.fragment, EGDSHeading.fragment, DirectFeedbackMultiSelectGroup.fragment, EGDSSpannableText.fragment, DirectFeedbackRadioGroup.fragment, DirectFeedbackInputField.fragment, DirectFeedbackTextArea.fragment, DirectFeedbackTextList.fragment);
var EgdsInputComponentLookup = {
    DirectFeedbackMultiSelectGroup: DirectFeedbackMultiSelectGroup,
    DirectFeedbackRadioGroup: DirectFeedbackRadioGroup,
    DirectFeedbackInputField: DirectFeedbackInputField,
    DirectFeedbackTextArea: DirectFeedbackTextArea
};
var EgdsInputValueLookupFn = {
    DirectFeedbackMultiSelectGroup: function (data, values) {
        return values.get(data.groupName);
    },
    DirectFeedbackRadioGroup: function (data, values) {
        return values.get(data.groupName);
    },
    DirectFeedbackInputField: function (data, values) {
        return data.name ? values.get(data.name) : null;
    },
    DirectFeedbackTextArea: function (data, values) {
        return data.name ? values.get(data.name) : null;
    }
};
function isDisplayElement(data) {
    return !!DirectFeedbackDisplayElement.supportedTypes.find(function (typeName) { return data.__typename === typeName; });
}
var DirectFeedbackElement = function (props) {
    var data = props.data, onChange = props.onChange, values = props.values;
    var InputComponentImplementation = EgdsInputComponentLookup[data.__typename];
    var onChangeHandler = useCallback(function (name, value, isValid) {
        if (onChange)
            onChange(name, value, isValid);
    }, [onChange]);
    if (InputComponentImplementation) {
        var ValueLookup = EgdsInputValueLookupFn[data.__typename];
        return React.createElement(InputComponentImplementation, { data: data, value: ValueLookup(data, values), onChange: onChangeHandler });
    }
    if (isDisplayElement(data)) {
        return React.createElement(DirectFeedbackDisplayElement, { data: data });
    }
    return React.createElement("pre", null, JSON.stringify(data, null, 2));
};
DirectFeedbackElement.fragment = fragment$5;
DirectFeedbackElement.displayName = 'DirectFeedbackElement';
var templateObject_1$6;

var fragment$4 = gql(templateObject_1$5 || (templateObject_1$5 = __makeTemplateObject(["\n  fragment UIPrimaryButtonFragment on UIPrimaryButton {\n    __typename\n    action {\n      __typename\n      analytics {\n        __typename\n        referrerId\n        linkName\n      }\n      accessibility\n    }\n    icon {\n      ...EGDSIconFragment\n    }\n    primary\n  }\n\n  ", "\n"], ["\n  fragment UIPrimaryButtonFragment on UIPrimaryButton {\n    __typename\n    action {\n      __typename\n      analytics {\n        __typename\n        referrerId\n        linkName\n      }\n      accessibility\n    }\n    icon {\n      ...EGDSIconFragment\n    }\n    primary\n  }\n\n  ", "\n"])), EGDSIcon.fragment);
var UIPrimaryButton = (function ActionComponentFactory() {
    function UIPrimaryButtonRender(_a) {
        var actionDispatch = _a.actionDispatch, _b = _a.buttonProps, buttonProps = _b === void 0 ? {} : _b, data = _a.data;
        var action = data.action;
        var onClick = buttonProps.onClick;
        var onClickHandler = useCallback(function (evt) {
            if (actionDispatch && action) {
                actionDispatch(action);
            }
            if (onClick) {
                onClick(evt);
            }
        }, [actionDispatch, action, onClick]);
        return (React.createElement(UitkPrimaryButton, __assign({ type: "button" }, buttonProps, { onClick: onClickHandler }),
            data.primary,
            " ",
            data.icon && React.createElement(EGDSIcon, { data: data.icon })));
    }
    UIPrimaryButtonRender.displayName = 'UIPrimaryButton';
    UIPrimaryButtonRender.fragment = fragment$4;
    return UIPrimaryButtonRender;
})();
var templateObject_1$5;

var fragment$3 = gql(templateObject_1$4 || (templateObject_1$4 = __makeTemplateObject(["\n  fragment UISecondaryButtonFragment on UISecondaryButton {\n    __typename\n    action {\n      __typename\n      analytics {\n        __typename\n        referrerId\n        linkName\n      }\n      accessibility\n    }\n    icon {\n      ...EGDSIconFragment\n    }\n    primary\n  }\n\n  ", "\n"], ["\n  fragment UISecondaryButtonFragment on UISecondaryButton {\n    __typename\n    action {\n      __typename\n      analytics {\n        __typename\n        referrerId\n        linkName\n      }\n      accessibility\n    }\n    icon {\n      ...EGDSIconFragment\n    }\n    primary\n  }\n\n  ", "\n"])), EGDSIcon.fragment);
var UISecondaryButton = (function ActionComponentFactory() {
    function UISecondaryButtonRender(_a) {
        var actionDispatch = _a.actionDispatch, _b = _a.buttonProps, buttonProps = _b === void 0 ? {} : _b, data = _a.data;
        var action = data.action;
        var onClick = buttonProps.onClick;
        var onClickHandler = useCallback(function (evt) {
            if (actionDispatch && action) {
                actionDispatch(action);
            }
            if (onClick) {
                onClick(evt);
            }
        }, [actionDispatch, action, onClick]);
        return (React.createElement(UitkSecondaryButton, __assign({ type: "button" }, buttonProps, { onClick: onClickHandler }),
            data.primary,
            " ",
            data.icon && React.createElement(EGDSIcon, { data: data.icon })));
    }
    UISecondaryButtonRender.displayName = 'UISecondaryButton';
    UISecondaryButtonRender.fragment = fragment$3;
    return UISecondaryButtonRender;
})();
var templateObject_1$4;

var fragment$2 = gql(templateObject_1$3 || (templateObject_1$3 = __makeTemplateObject(["\n  fragment EGDSButtonFragment on EGDSButton {\n    __typename\n    accessibility\n    analytics {\n      __typename\n      referrerId\n      linkName\n    }\n    action {\n      __typename\n      analytics {\n        __typename\n        referrerId\n        linkName\n      }\n      accessibility\n    }\n    disabled\n    icon {\n      ...EGDSIconFragment\n    }\n    primary\n\n    ...UIPrimaryButtonFragment\n    ...UISecondaryButtonFragment\n  }\n\n  ", "\n  ", "\n"], ["\n  fragment EGDSButtonFragment on EGDSButton {\n    __typename\n    accessibility\n    analytics {\n      __typename\n      referrerId\n      linkName\n    }\n    action {\n      __typename\n      analytics {\n        __typename\n        referrerId\n        linkName\n      }\n      accessibility\n    }\n    disabled\n    icon {\n      ...EGDSIconFragment\n    }\n    primary\n\n    ...UIPrimaryButtonFragment\n    ...UISecondaryButtonFragment\n  }\n\n  ", "\n  ", "\n"])), UIPrimaryButton.fragment, UISecondaryButton.fragment);
var EGDSButtonConcreteTypeLookup = {
    UIPrimaryButton: UIPrimaryButton,
    UISecondaryButton: UISecondaryButton
};
var EGDSButton = (function ActionComponentFactory() {
    function EGDSButtonRender(_a) {
        var actionDispatch = _a.actionDispatch, buttonProps = _a.buttonProps, data = _a.data;
        var Component = EGDSButtonConcreteTypeLookup[data.__typename] || UISecondaryButton;
        var analytics = data.analytics;
        var track = useClickTracker();
        var uitkButtonProps = __assign({}, buttonProps);
        var onClick = uitkButtonProps.onClick;
        uitkButtonProps.onClick = useCallback(function (evt) {
            if (analytics) {
                track("" + analytics.referrerId, analytics.linkName, false);
            }
            if (onClick) {
                onClick(evt);
            }
        }, [analytics, track, onClick]);
        return React.createElement(Component, { data: data, buttonProps: uitkButtonProps, actionDispatch: actionDispatch });
    }
    EGDSButtonRender.displayName = 'EGDSButton';
    EGDSButtonRender.fragment = fragment$2;
    return EGDSButtonRender;
})();
var templateObject_1$3;

var fragment$1 = gql(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject(["\n  fragment DirectFeedbackPageFragment on DirectFeedbackFormPage {\n    title\n    subtitle {\n      ...EGDSTextFragment\n    }\n    id\n    commands {\n      action {\n        ... on DirectFeedbackOpenNextPageAction {\n          __typename\n          destinationId\n        }\n\n        ... on DirectFeedbackOpenPreviousPageAction {\n          __typename\n        }\n\n        ... on DirectFeedbackSubmitFormAction {\n          __typename\n        }\n      }\n\n      ...EGDSButtonFragment\n    }\n    displayConditions {\n      fieldId\n      expectedValue\n      conditionType\n    }\n    elements {\n      __typename\n      ...DirectFeedbackElementFragment\n    }\n    footer {\n      __typename\n      ...DirectFeedbackDisplayElementFragment\n    }\n  }\n  ", "\n  ", "\n  ", "\n  ", "\n"], ["\n  fragment DirectFeedbackPageFragment on DirectFeedbackFormPage {\n    title\n    subtitle {\n      ...EGDSTextFragment\n    }\n    id\n    commands {\n      action {\n        ... on DirectFeedbackOpenNextPageAction {\n          __typename\n          destinationId\n        }\n\n        ... on DirectFeedbackOpenPreviousPageAction {\n          __typename\n        }\n\n        ... on DirectFeedbackSubmitFormAction {\n          __typename\n        }\n      }\n\n      ...EGDSButtonFragment\n    }\n    displayConditions {\n      fieldId\n      expectedValue\n      conditionType\n    }\n    elements {\n      __typename\n      ...DirectFeedbackElementFragment\n    }\n    footer {\n      __typename\n      ...DirectFeedbackDisplayElementFragment\n    }\n  }\n  ", "\n  ", "\n  ", "\n  ", "\n"])), DirectFeedbackElement.fragment, DirectFeedbackDisplayElement.fragment, EGDSButton.fragment, EGDSText.fragment);
var DirectFeedbackPage = function (_a) {
    var _b, _c, _d, _e, _f;
    var data = _a.data, eventDispatch = _a.eventDispatch, values = _a.values, valuesValidation = _a.valuesValidation;
    var dispatchLookup = useMemo(function () {
        return {
            DirectFeedbackOpenNextPageAction: function (action) {
                if (eventDispatch) {
                    eventDispatch(new DirectFeedbackNextCommand(action));
                }
            },
            DirectFeedbackOpenPreviousPageAction: function (action) {
                if (eventDispatch) {
                    eventDispatch(new DirectFeedbackPreviousCommand(action));
                }
            },
            DirectFeedbackSubmitFormAction: function (action) {
                if (eventDispatch) {
                    eventDispatch(new DirectFeedbackSubmitCommand(action));
                }
            }
        };
    }, [eventDispatch]);
    var onChange = useCallback(function (name, value, isValid) {
        if (eventDispatch) {
            eventDispatch(new DirectFeedbackSetValueCommand(name, value, isValid));
        }
    }, [eventDispatch]);
    var isValid = (_b = data.elements) === null || _b === void 0 ? void 0 : _b.filter(function (element) {
        return 'name' in element || 'groupName' in element;
    }).map(function (element) {
        if ('name' in element) {
            return element.name;
        }
        /**
         * We are explicitly ignoring these warnings due to the above filters
         * that reduce the set of elements we may encounter on this line
         * to only elements that have the `groupName` property.
         */
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return element.groupName;
    }).reduce(function (aggregate, fieldId) {
        return aggregate && (!valuesValidation.has(fieldId) || valuesValidation.get(fieldId));
    }, true);
    return (React.createElement(UitkSpacing, { margin: "two" },
        React.createElement(UitkForm, { hasRequiredFieldIndicator: true },
            React.createElement(UitkLayoutFlex, { space: "four", direction: "column" },
                (data.title || ((_c = data.subtitle) === null || _c === void 0 ? void 0 : _c.text)) && (React.createElement(UitkLayoutFlexItem, null,
                    React.createElement("div", null,
                        data.title && (React.createElement(UitkHeading, { tag: "h3", size: 4 }, data.title)),
                        data.subtitle && React.createElement(UitkSubheading, null, data.subtitle.text)))),
                ((_d = data.elements) === null || _d === void 0 ? void 0 : _d.length)
                    ? data.elements.map(function (element, index) {
                        return (React.createElement(UitkLayoutFlexItem, { key: element.__typename + "_" + index },
                            React.createElement("div", null,
                                React.createElement(DirectFeedbackElement, { data: element, values: values, onChange: onChange }))));
                    })
                    : null,
                ((_e = data.commands) === null || _e === void 0 ? void 0 : _e.length) ? (React.createElement(UitkLayoutFlexItem, null,
                    React.createElement(UitkSpacing, { margin: { blockstart: 'four', blockend: 'four' } },
                        React.createElement(UitkLayoutFlex, { justifyContent: "space-between" }, data.commands.map(function (command, index) {
                            var _a;
                            var actionType = ((_a = command === null || command === void 0 ? void 0 : command.action) === null || _a === void 0 ? void 0 : _a.__typename) || '';
                            var actionDispatch = dispatchLookup[actionType];
                            var disabled = (actionType === 'DirectFeedbackOpenNextPageAction' || actionType === 'DirectFeedbackSubmitFormAction') && !isValid;
                            return (React.createElement(UitkLayoutFlexItem, { key: "EGDSButton_" + index },
                                React.createElement(EGDSButton, { data: command, actionDispatch: actionDispatch, buttonProps: { disabled: disabled } })));
                        }))))) : null,
                ((_f = data.footer) === null || _f === void 0 ? void 0 : _f.length)
                    ? data.footer.map(function (element, index) {
                        return (React.createElement(UitkLayoutFlexItem, { key: element.__typename + "_" + index },
                            React.createElement("div", null,
                                React.createElement(DirectFeedbackDisplayElement, { data: element }))));
                    })
                    : null))));
};
DirectFeedbackPage.fragment = fragment$1;
DirectFeedbackPage.displayName = 'DirectFeedbackPage';
var templateObject_1$2;

var fragment = gql(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  fragment DirectFeedbackPagesFragment on DirectFeedbackPrompt {\n    pages {\n      ...DirectFeedbackPageFragment\n    }\n  }\n\n  ", "\n"], ["\n  fragment DirectFeedbackPagesFragment on DirectFeedbackPrompt {\n    pages {\n      ...DirectFeedbackPageFragment\n    }\n  }\n\n  ", "\n"])), DirectFeedbackPage.fragment);
var getInitialState = function (pages) {
    var requiredElements = pages
        .flatMap(function (page) {
        return page.elements;
    })
        .filter(function (element) {
        return 'name' in element || 'groupName' in element;
    })
        .filter(function (element) {
        return 'required' in element && element.required;
    })
        .map(function (element) {
        if ('name' in element) {
            return element.name;
        }
        /**
         * We are explicitly ignoring these warnings due to the above filters
         * that reduce the set of elements we may encounter on this line
         * to only elements that have the `groupName` property.
         */
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return element.groupName;
    });
    var recordValidation = new Map();
    requiredElements.forEach(function (fieldId) {
        recordValidation.set(fieldId, false);
    });
    return {
        pageIndexStack: [0],
        numPages: pages.length,
        record: new Map(),
        recordValidation: recordValidation
    };
};
var isPageValid = function (page, record) {
    return page.displayConditions
        ? page.displayConditions.every(function (condition) {
            var fieldValue = record.get(condition.fieldId);
            var isValid;
            switch (condition.conditionType) {
                case DirectFeedbackPageDisplayConditionType.BOOL:
                    isValid = condition.expectedValue === fieldValue;
                    break;
                case DirectFeedbackPageDisplayConditionType.PATTERN:
                    isValid = new RegExp(condition.expectedValue).test(fieldValue);
                    break;
                case DirectFeedbackPageDisplayConditionType.TEXT:
                    isValid = condition.expectedValue === fieldValue;
                    break;
                case DirectFeedbackPageDisplayConditionType.NONE:
                    // todo: log error. sentry?
                    isValid = true;
                    break;
                default:
                    // todo: log error. sentry?
                    isValid = true;
                    break;
            }
            return isValid;
        })
        : true;
};
var getNextPageIndex = function (pages, state) {
    var possiblePages = pages.slice(state.pageIndexStack[state.pageIndexStack.length - 1] + 1);
    var nextPageIndex = -1;
    var possiblePagesIndex = possiblePages.findIndex(function (page) { return isPageValid(page, state.record); });
    if (possiblePagesIndex >= 0) {
        nextPageIndex = possiblePagesIndex + 1 + state.pageIndexStack[state.pageIndexStack.length - 1];
    }
    else {
        nextPageIndex = pages.length;
    }
    return nextPageIndex;
};
var DirectFeedbackPages = function (_a) {
    var data = _a.data, doSubmit = _a.doSubmit, doClose = _a.doClose;
    var pages = data.pages;
    var pagesStateReducer = useCallback(function (state, action) {
        var newState = {
            pageIndexStack: state.pageIndexStack.slice(0),
            numPages: state.numPages,
            record: new Map(state.record),
            recordValidation: new Map(state.recordValidation)
        };
        var setter;
        var goto;
        switch (action.type) {
            case DirectFeedbackCommandType.Submit:
                newState.pageIndexStack.push(getNextPageIndex(pages, state));
                break;
            case DirectFeedbackCommandType.Next:
                goto = action;
                if (goto.action.destinationId) {
                    var nextPageIndex = pages.findIndex(function (page) { return page.id === goto.action.destinationId; });
                    newState.pageIndexStack.push(nextPageIndex);
                }
                else {
                    newState.pageIndexStack.push(getNextPageIndex(pages, state));
                }
                break;
            case DirectFeedbackCommandType.Previous:
                if (newState.pageIndexStack.length > 1) {
                    newState.pageIndexStack.pop();
                }
                break;
            case DirectFeedbackCommandType.SetValue:
                setter = action;
                newState.record.set(setter.fieldId, setter.value);
                newState.recordValidation.set(setter.fieldId, setter.isValid);
                break;
        }
        return newState;
    }, [pages]);
    var _b = __read(useReducer(pagesStateReducer, pages, getInitialState), 2), state = _b[0], dispatch = _b[1];
    var pageEventHandler = useCallback(function (action) {
        switch (action.type) {
            case DirectFeedbackCommandType.Submit:
                if (doSubmit) {
                    doSubmit(Array.from(state.record.keys()).map(function (key) {
                        var _a;
                        return {
                            key: key,
                            value: (_a = state.record.get(key)) === null || _a === void 0 ? void 0 : _a.toString()
                        };
                    }));
                }
                break;
        }
        dispatch(action);
    }, [dispatch, doSubmit, state]);
    useEffect(function () {
        if (state.pageIndexStack[state.pageIndexStack.length - 1] >= state.numPages && doClose) {
            doClose();
        }
    });
    if (state.pageIndexStack[state.pageIndexStack.length - 1] < state.numPages) {
        return (React.createElement(DirectFeedbackPage, { data: pages[state.pageIndexStack[state.pageIndexStack.length - 1]], eventDispatch: pageEventHandler, values: state.record, valuesValidation: state.recordValidation }));
    }
    return null;
};
DirectFeedbackPages.fragment = fragment;
DirectFeedbackPages.displayName = 'DirectFeedbackPages';
var templateObject_1$1;

/**
 * @protected
 * Exposing for package internal usage and unit tests only.
 * Do not export queries at the top level of the package.
 */
var __Query = gql(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  query DirectFeedbackQuery($context: ContextInput!, $promptId: ID!) {\n    directFeedbackPromptById(context: $context, id: $promptId) {\n      title\n      ...DirectFeedbackPagesFragment\n    }\n  }\n\n  ", "\n"], ["\n  query DirectFeedbackQuery($context: ContextInput!, $promptId: ID!) {\n    directFeedbackPromptById(context: $context, id: $promptId) {\n      title\n      ...DirectFeedbackPagesFragment\n    }\n  }\n\n  ", "\n"])), DirectFeedbackPages.fragment);
/**
 * @protected
 * Exposing for package internal usage and unit tests only.
 * Do not export mutations at the top level of the package.
 */
var __SubmitFeedbackMutation = gql(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  mutation DirectFeedbackSubmit($context: ContextInput!, $id: ID!, $feedback: DirectFeedbackValuesInput!) {\n    directFeedbackSubmit(context: $context, id: $id, feedback: $feedback) {\n      errors {\n        fieldId\n        error\n      }\n    }\n  }\n"], ["\n  mutation DirectFeedbackSubmit($context: ContextInput!, $id: ID!, $feedback: DirectFeedbackValuesInput!) {\n    directFeedbackSubmit(context: $context, id: $id, feedback: $feedback) {\n      errors {\n        fieldId\n        error\n      }\n    }\n  }\n"])));
// TODO: Add a property to take an initial record to support use cases such as user settings.
var PromptContent = function (_a) {
    var data = _a.data, mutationErrors = _a.mutationErrors, doSubmit = _a.doSubmit, doClose = _a.doClose;
    return (React.createElement(UitkLayoutGrid, { space: "two" },
        mutationErrors && mutationErrors.length > 0
            ? mutationErrors.map(function (error, index) {
                return (React.createElement(UitkLayoutGridItem, { key: "UitkErrorSummary_" + index },
                    React.createElement(UitkErrorSummary, { heading: "" // error.fieldId || '' . Need to map it somehow or instead highlight the actual input element.
                        , description: error.error })));
            })
            : null,
        React.createElement(UitkLayoutGridItem, null,
            React.createElement(DirectFeedbackPages, { data: data, doClose: doClose, doSubmit: doSubmit }))));
};
var ErrorContent = function (_a) {
    var error = _a.error;
    return React.createElement(UitkErrorSummary, { heading: (error === null || error === void 0 ? void 0 : error.message) || '' });
};
var LoadingContent = function () {
    return (React.createElement(UitkLayoutFlex, { justifyContent: "center" },
        React.createElement(UitkLayoutFlexItem, null,
            React.createElement(UitkLoader, null))));
};
var DirectFeedbackPrompt = function (props) {
    var inputs = props.inputs, eventDispatch = props.eventDispatch, contextValues = props.contextValues;
    var context = useBexApi().context;
    var promptId = inputs.promptId;
    var variables = __assign(__assign({}, inputs), { context: context });
    /**
     * "data": {
     *     "directFeedbackSubmit": {
     *       "errors": []
     *     }
     *   },
     */
    var _a = __read(useMutation(__SubmitFeedbackMutation), 2), submit = _a[0], _b = _a[1], mutationData = _b.data; _b.loading; _b.error;
    var doEventDispatch = useCallback(function (event) {
        if (eventDispatch) {
            setTimeout(function () { return eventDispatch(event); });
        }
    }, [eventDispatch]);
    var emitOnError = useCallback(function (eventError) {
        doEventDispatch(new DirectFeedbackErrorEvent(promptId, eventError));
    }, [promptId, doEventDispatch]);
    var emitOnLoad = useCallback(function (data) {
        if (data.directFeedbackPromptById) {
            doEventDispatch(new DirectFeedbackLoadEvent(promptId, data.directFeedbackPromptById));
        }
    }, [promptId, doEventDispatch]);
    var _c = useQuery(__Query, {
        variables: variables,
        onError: emitOnError,
        onCompleted: emitOnLoad
    }), data = _c.data, error = _c.error, loading = _c.loading;
    var doSubmit = useCallback(function (formValues) {
        submit({
            variables: {
                context: context,
                feedback: {
                    formValues: formValues,
                    context: contextValues
                },
                id: inputs === null || inputs === void 0 ? void 0 : inputs.promptId
            }
        }).then(function () {
            if (eventDispatch) {
                eventDispatch(new DirectFeedbackSubmitEvent(promptId, contextValues, formValues));
            }
        });
    }, [promptId, eventDispatch, submit, contextValues, context, inputs === null || inputs === void 0 ? void 0 : inputs.promptId]);
    var emitOnClose = useCallback(function () {
        if (eventDispatch) {
            eventDispatch(new DirectFeedbackCloseEvent(promptId));
        }
    }, [promptId, eventDispatch]);
    var content;
    if (loading) {
        content = React.createElement(LoadingContent, null);
    }
    else if (data === null || data === void 0 ? void 0 : data.directFeedbackPromptById) {
        content = (React.createElement(PromptContent, { data: data.directFeedbackPromptById, mutationErrors: (mutationData === null || mutationData === void 0 ? void 0 : mutationData.directFeedbackSubmit.errors) || [], doSubmit: doSubmit, doClose: emitOnClose }));
    }
    else {
        content = React.createElement(ErrorContent, { error: error });
    }
    return content;
};
DirectFeedbackPrompt.displayName = 'DirectFeedbackPrompt';
var templateObject_1, templateObject_2;

/**
 * Dialog to present a form driven by Content-Direct-Feedback
 *
 * Makes use of the useDirectFeedbackDialog hook to control the dialog with an
 *
 * Alternatively, use the `useDialog` hook to control the dialog with an id equal to the promptId
 * and a dialogQueryKey of 'cdfPromptDialog'
 * @param props
 */
var DirectFeedbackDialog = function (props) {
    var _a;
    var inputs = props.inputs, eventDispatch = props.eventDispatch, contextValues = props.contextValues;
    var formatText = useLocalization().formatText;
    var promptId = inputs.promptId;
    var dialogQueryKey = 'cdfPromptDialog';
    var _b = __read(useDialog(promptId, dialogQueryKey), 3), isDialogOpen = _b[0], dialogActions = _b[1], DialogWrapper = _b[2];
    var _c = __read(useState(''), 2), toolbarTitle = _c[0], setToolbarTitle = _c[1];
    var closeDialog = dialogActions.closeDialog;
    var track = useClickTracker();
    var onNavigationClick = useCallback(function (id, description, event) {
        track(id, 'Direct feedback prompt close', false);
    }, [track]);
    var directFeedbackEventHandler = useCallback(function (event) {
        var _a;
        switch (event.type) {
            case DirectFeedbackEventType.Close:
                closeDialog();
                break;
            case DirectFeedbackEventType.Load:
                setToolbarTitle(((_a = event.data) === null || _a === void 0 ? void 0 : _a.title) || '');
                break;
        }
        if (eventDispatch) {
            setTimeout(function () { return eventDispatch(event); });
        }
    }, [closeDialog, eventDispatch]);
    /* istanbul ignore next */
    var emitOnClose = useCallback(function () {
        if (eventDispatch) {
            eventDispatch(new DirectFeedbackCloseEvent(promptId));
        }
    }, [promptId, eventDispatch]);
    var handleToolbarClick = useCallback(function () {
        dialogActions.closeDialog();
    }, [dialogActions]);
    return (React.createElement(DialogWrapper, null,
        React.createElement(UitkFullscreenDialog, { dialogShow: isDialogOpen, closeCallback: emitOnClose, returnFocusOnClose: true },
            React.createElement(UitkToolbar, { navigationContent: {
                    onClick: handleToolbarClick,
                    navType: 'close',
                    navIconLabel: (_a = formatText('close.dialog')) !== null && _a !== void 0 ? _a : 'Close',
                    onClickAnalytics: {
                        id: "DirectFeedback." + promptId + ".dialog",
                        callback: onNavigationClick
                    }
                }, toolbarTitle: toolbarTitle }),
            React.createElement(UitkDialogContent, null,
                React.createElement(DirectFeedbackPrompt, { inputs: inputs, eventDispatch: directFeedbackEventHandler, contextValues: contextValues })))));
};
DirectFeedbackDialog.displayName = 'DirectFeedbackDialog';

export { DirectFeedbackDialog, DirectFeedbackPrompt };
